// ITEM 2 / FUNNEL-4 — **THE FIFTH ANSWER, AND THE CUT SHE ASKED TO BE ENFORCED RATHER THAN ASKED
// FOR.**
//
// HER ORDER, the tail of ITEM 2: *"five real turns reading them from real placements, the FIFTH
// answer ending in the banger question built from THEIR conversation, the wall with the gold button
// IN the chat to membership, the cliffhanger evaluator enforcing every cut."*
//
// FUNNEL-4 built the allotment, the opening and the wall. What it did not build is this: on the turn
// that spends her last free one, the answer has to END ON A QUESTION SHE CANNOT LEAVE ALONE, and
// that question has to come out of what SHE has been saying rather than out of a closer the model
// keeps in its pocket. A generic *"want to know more?"* is the exact opposite of a banger: it is the
// house asking to be asked, at the one moment the whole funnel turns on her wanting to keep going.
//
// >>> AND IT IS A GATE RATHER THAN A PROMPT LINE, WHICH IS THE GATE LAW'S OWN SPLIT AND THE THING
// HER SENTENCE NAMES. <<< *"The cliffhanger evaluator ENFORCING every cut"* is a sentence about
// enforcement: FF1-RECEIPT measured the funnel's own cut law composed, correct, deployed and wired
// to a logger nobody passed, so the rate was unmeasurable and the retries were unbought receipts.
// The directive PREVENTS and this law ENFORCES, and both halves live here so they cannot drift.
//
// >>> THE CUT IS NOT RE-DECIDED HERE. IT IS `funnelTeaser`'s, DELEGATED (R28/R29). <<< This house
// already has ONE cliffhanger evaluator — FUNNEL-FIX-1's `teaserFinished`, with her own exhibit as
// its calibration, its resolution openers, its sign-offs and its earlier-sentence rule. A second
// spelling of *does this paragraph read as finished* is two answer keys on the one surface her
// advertising pays for, and the copy that drifts is the one nobody re-runs. What IS decided here is
// the half that is genuinely different (R18): a TEASER is one paragraph of 140-620 characters and a
// CHAT ANSWER is not, so the evaluator is asked of the answer's LAST PARAGRAPH — which is the text
// her clause is about, the cut — with a closer's own band rather than a teaser's shape law applied
// to a conversation.
//
// It imports only import-free house laws, so the whole chain loads inside a FAST `.mjs` fixture with
// no bundler, no browser and no database, and every outcome is RUN rather than read.

import { FREE_TURNS, freeTurnView } from "./lalaFreeTurns.js";
import { teaserParagraphs, teaserLastSentence, teaserFinished } from "./funnelTeaser.js";
import { searchTerms, termMatches } from "./ownWorkSearch.js";

/**
 * A closing paragraph's own band. **BOTH ENDS COST SOMETHING** (R43's M8): a ceiling alone is
 * satisfied by one word, and one word is a truncation rather than a cut — while a floor alone lets
 * the last paragraph run away with the answer E6 spent a round sizing. The floor is deliberately far
 * below `TEASER_MIN_CHARS`, because a real banger is often the question ALONE (*"So what happens the
 * day he actually calls?"* is 48 characters) and refusing that would refuse the shape she ordered.
 */
export const CLOSER_MIN = 24;
export const CLOSER_MAX = 700;

/**
 * **THE CLOSERS THAT ARE NOT BANGERS.** Every one of these is a question that could end ANY
 * conversation on this site, which is precisely what her clause forbids: *built from THEIR
 * conversation*. Matched inside the closing question only — *"does that make sense"* mid-answer is
 * ordinary warmth and the same words as the last thing she reads is the house asking to be asked.
 */
/**
 * **A BARE ACKNOWLEDGEMENT IS NOT A SUBJECT, AND CLAUSE 3's OWN ESCAPE COULD NOT SAY SO.**
 *
 * That escape reads *an unreadable ask convicts nobody* (R19) — and MEASURED against every turn this
 * gate has ever convicted, it could not fire: a conversation whose only member turn is the word
 * *"No"* yields the term `no`, so the law read her as having handed it a subject and refused her
 * fifth answer for not repeating it. Seven convictions, and it has never once been right.
 *
 * **`searchTerms` IS NOT WHAT IS WRONG AND IS NOT TOUCHED (R19, scope rule 4).** Its floor of 2 and
 * its stop list are calibrated for a JOURNAL SEARCH, where a member TYPED the word on purpose and
 * meant it (IT #120) — and `no` really is a term there. Here the input is not a query, it is
 * CHATTER, so the same token arrives by accident. Two inputs, two questions (R18), and widening that
 * roster from an ITEM 2 round would change a feature this round never measured.
 *
 * **SO THIS DELEGATES AND NARROWS RATHER THAN ANSWERING AGAIN (R28/R29):** one tokeniser, one stop
 * list, and NO stemmer — which is `termMatches`' own ruling, that a stemmer is a second answer to
 * what a word is and would need a gate of its own. What is added is a roster of utterances that are
 * not subjects, which is the relationship `QUEUE_MOD_ACTIONS` has to `MOD_ACTIONS`: same law, one
 * roster narrower.
 *
 * **AND IT IS A ROSTER RATHER THAN A LENGTH FLOOR, WHICH THIS ROUND'S OWN FIXTURE IS WHY.** The
 * first cut was `length >= 3` and it spared *"No"* while convicting *"Yes"* — a one-letter accident
 * rather than a rule — and a floor of four buys that back by throwing away `sun`, `job` and
 * `4th`, which are subjects a member really types. Every entry below is a non-subject BY NAME, so
 * the narrowing cannot cost her a word she meant.
 *
 * **IT IS NOT E6's `ACCEPTANCE` (R18).** That roster answers *did she accept the offer this house
 * just made* — it is anchored on the WHOLE message and it deliberately excludes *no*, which is the
 * one word this law was measured failing on. Reusing it is the `hide`-for-`decline` trap.
 */
export const NOT_A_SUBJECT = new Set([
  "no", "nope", "nah", "yes", "yeah", "yep", "yup", "ok", "okay", "sure", "fine",
  "hi", "hey", "hello", "thanks", "thank", "please", "so", "re", "um", "uh", "oh", "ah", "mm", "lol",
]);

/**
 * The terms of hers a closing question can honestly be held to. **ITS WHOLE EFFECT IS TO LET CLAUSE
 * 3's ESCAPE FIRE WHERE ITS OWN COMMENT SAYS IT MUST** — every word it removes is one she cannot be
 * held to having meant.
 */
export function subjectTerms(asked) {
  return searchTerms(asked).filter((term) => !NOT_A_SUBJECT.has(term));
}

export const GENERIC_CLOSERS = [
  "want to know more", "wanna know more", "know more about",
  "anything else", "something else", "any other question",
  "go deeper", "dig deeper", "keep going", "carry on",
  "does that make sense", "make sense", "how does that land", "how does that sit",
  "what do you think", "what are your thoughts", "sound good", "sound right",
  "shall we", "should we", "want me to", "would you like me to", "do you want me to",
  "let me know", "on your mind", "help you with",
];

/**
 * **IS THIS THE TURN THAT SPENT HER LAST ONE?**
 *
 * Asked of the state the charge HANDS BACK, so it is a fact about what just happened rather than a
 * prediction. Presence, never coercion (R53/R136/R137): this arrives off a database row where
 * `Number(true)` is 1 and `Number("")` is 0, and a coerced value here fires the whole closing law on
 * a member with four turns still in hand — or, worse, silently never fires on the one turn it exists
 * for.
 */
export function lastFreeTurn(state) {
  if (!state || typeof state !== "object") return false;
  if (state.granted !== true) return false;
  return typeof state.used === "number" && Number.isFinite(state.used) && state.used >= FREE_TURNS;
}

/**
 * **HER OWN WORDS, ACROSS THE WHOLE CONVERSATION.** Her clause is *built from THEIR conversation*
 * and not *from their last message*, so the answer key is every member turn plus the ask in hand.
 * That is also the direction that costs nobody anything (R19): a wider answer key can only ever make
 * the closing question EASIER to justify, and what this law is for is catching a question that came
 * out of the model's pocket rather than out of her.
 */
export function theirWords(history, ask = "") {
  const rows = Array.isArray(history) ? history : [];
  const said = [];
  for (const row of rows) {
    if (!row || typeof row !== "object") continue;
    if (row.role !== "user" && row.role !== "member") continue;
    const body = typeof row.content === "string" ? row.content : typeof row.text === "string" ? row.text : "";
    if (body.trim()) said.push(body);
  }
  if (typeof ask === "string" && ask.trim()) said.push(ask);
  return said.join("\n");
}

/** The last sentence, which on a well-built fifth answer IS the question. `funnelTeaser`'s reader. */
export function closingSentence(raw) {
  return teaserLastSentence(String(raw ?? "").trim());
}

/** The paragraph the cut really lives in. */
export function closingParagraph(raw) {
  const paras = teaserParagraphs(String(raw ?? "").trim());
  return paras.length ? paras[paras.length - 1] : "";
}

/**
 * **THE VERDICT, AS NAMED FACTS RATHER THAN A BOOLEAN (R18).**
 *
 * *It does not ask anything*, *it asks something it could have asked anybody* and *it wraps the
 * conversation up* are three different failures with three different repairs, and a rewrite told
 * only that it was wrong has to guess again — which is the gate law's own rule and what produced
 * her funnel exhibit.
 *
 * >>> THE ORDER IS THE SHAPE OF THE ANSWER AND NOT AN ACCIDENT. <<< The question is asked for FIRST,
 * because on an answer that ends in a full stop every verdict below is about the wrong sentence:
 * naming its opener as a resolution would send a rewrite to repair a sentence that is not the fault.
 *
 * `{ label, match }` is `ANSWER_GATE_LAWS`' own shape, so this is one entry on that roster rather
 * than a second enforcer beside it — five laws, one re-render, each filed under its own gate name
 * (ITEM 5's ruling: five enforcers in a row is five model calls each undoing the last one's repair).
 */
export function bangerViolations(reply, { asked = "" } = {}) {
  const text = String(reply ?? "").trim();
  if (!text) return [];

  const out = [];
  const last = closingSentence(text);
  const lower = last.toLowerCase();

  // 1. It has to ASK something, and it has to ask it LAST.
  if (!last.endsWith("?")) {
    return [{ label: "the last answer does not end on a question", match: last.slice(-80) }];
  }

  // 2. A question anybody could have been asked is not built from anything.
  for (const g of GENERIC_CLOSERS) {
    if (lower.includes(g)) { out.push({ label: "the closing question is a stock closer", match: g }); break; }
  }

  // 3. Her own words have to be IN it. **AN UNREADABLE ASK CONVICTS NOBODY** (R19): a member whose
  //    conversation carries no term this house can read has done nothing wrong, and refusing her the
  //    fifth answer over it is the expensive direction on the one turn that decides whether she
  //    stays.
  const terms = subjectTerms(asked);
  if (terms.length && !terms.some((term) => termMatches(lower, term))) {
    out.push({ label: "the closing question carries nothing they actually said", match: last.slice(0, 80) });
  }

  // 4. THE CUT, delegated whole. `teaserFinished` is asked of the CLOSING PARAGRAPH with a closer's
  //    band, so its sign-off roster, its resolution openers and its earlier-sentence rule all run on
  //    the text her clause is about — and a teaser's one-paragraph shape law never runs on a chat.
  const closing = closingParagraph(text);
  // >>> THE RESOLUTION ROSTER IS TURNED OFF HERE, AND THE REASON IS THAT IT COULD ONLY EVER CONVICT
  // THE SHAPE SHE ORDERED. <<< `teaserFinished` reads the LAST SENTENCE, and clause 1 above has
  // already refused anything whose last sentence is not a QUESTION — so on this path that roster is
  // asked of an interrogative every single time. A resolution opener is a sentence WRAPPING UP, and
  // "So what does he do the night the lease is signed?" is the opposite of a wrap-up: it is the most
  // natural way a reader asks the next thing, and `so ` is on that roster. This law's own fixture
  // caught it convicting a textbook banger.
  //
  // IT COSTS NOTHING REAL. A genuine summing-up FOLLOWED by a question ("The bottom line is he is
  // leaving. So what do you do?") was never reachable either way, because only the final sentence is
  // read — and a stock question is already refused one clause up by name. What SURVIVES is every
  // half of the evaluator that can still fire on a question: the sign-offs, the band, and the shape.
  const cut = teaserFinished(closing, { min: CLOSER_MIN, max: CLOSER_MAX, resolutions: [] });
  if (!cut.ok) {
    out.push({ label: `the last answer reads as finished (${cut.why})`, match: String(cut.found ?? "").slice(0, 80) });
  }
  return out;
}

/**
 * **THE PREVENTION HALF.** Injected into the system prompt on that turn only, above the draft, the
 * way E6's size directive is — so the model is TOLD before it writes and the gate above is what
 * catches it when telling was not enough. It names the ACT rather than the machinery (S1-A/R117):
 * the model is not told about a `GENERIC_CLOSERS` roster, it is told not to ask a question it could
 * have asked anybody.
 */
export const LAST_TURN_DIRECTIVE = `

THIS IS THE LAST ANSWER THEY GET FROM YOU IN THIS CONVERSATION. Answer their question fully and
warmly first, exactly as you would on any other turn. Then END ON ONE QUESTION, and make it the
question they cannot walk away from.

Build that question out of what THEY have told you in this conversation - their situation, their
person, their timing, the thing they keep circling back to. Use their own words in it. It has to be a
question only they could have been asked.

Never end on a question you could ask anybody: not "want to know more", not "does that make sense",
not "anything else", not "shall we go deeper". Never say goodbye, never bless them off, and never
sum the conversation up. Stop on the question. Nothing after it.`;

export function lastTurnDirective() {
  return LAST_TURN_DIRECTIVE;
}

/**
 * **HER GLOWING PLUM WINDOW, AND THE DISCRIMINATOR IS THE ALLOTMENT ITSELF.**
 *
 * Her clause is *"then LALA ARRIVES in the glowing plum window speaking FIRST BY NAME"*. Measured
 * before a line was written (R25/R180), the arrival is the ordinary Ask Lala room, which wears her
 * own artwork on a fixed backdrop: not a window, and not glowing.
 *
 * >>> WHAT MAKES IT THE `/talk` COHORT IS THE FREE-TURN ALLOTMENT, WHICH ALREADY RIDES THE PAYLOAD.
 * <<< F4-3's landing mark is a `sessionStorage` stash and is SPENT by the read that used it, so it
 * is gone by the time this room renders — and a second, durable mark would be a new fact written on
 * her account for a class this house can already answer. FUNNEL-4's own ruling is that the allotment
 * IS this experience: it is granted at the gate branch for a free account touching Ask Lala with
 * nothing said between her and Lala yet, which is her clause 1 and her clause 5 in one moment. So
 * the window is the FACE of the free-turn conversation, and a member who has paid gets her room
 * back.
 *
 * **THE DIRECTION IS THE CHEAP ONE (R19/R8-4).** Over-applying paints a plum window for a free
 * member who reached Ask Lala some other way — visible, reportable, and arguably correct, since she
 * really is in the five-turn conversation with the wall at the end of it. Under-applying leaves her
 * clause unmet with no symptom at all.
 *
 * It DELEGATES to `freeTurnView` rather than reading `payload.granted` itself: that law already
 * refuses a payload that is not an object and a `granted` that is not exactly true (R53/R136/R137),
 * and a second reading of *is she on the allotment* is the copy that drifts (R28/R29).
 */
export function talkWindowClass(payload) {
  return freeTurnView(payload) ? " is-talk" : "";
}

/**
 * >>> HER ITEM 2 CLAUSE -- *THEN LALA ARRIVES ... SPEAKING FIRST BY NAME* -- DID NOT HAPPEN, AND
 * NOTHING ANYWHERE WAS RED. <<<
 *
 * MEASURED AT COORDINATES BEFORE A LINE WAS WRITTEN (R25/R180), on a staged funnel seat holding the
 * allotment: **ZERO `/api/lala/` requests on the Ask Lala PAGE's arrival.** The greeting composer is
 * live and correct -- `handleLalaMessages` writes it into an EMPTY thread held by a GRANTED account
 * -- and the page's mount effect called `loadConversations()` and nothing else, so nobody ever asked
 * it. R38/R165's trap in its purest form: composed, deployed, reachable from the DRAWER (which reads
 * on open) and from no other surface on this site. Her screen read the room's own airy empty state,
 * *"Hi Talk. What are we manifesting today?"*, and `free.payload` stayed null -- which is why the
 * plum window `talkWindowClass` decides never painted either.
 *
 * >>> THE COHORT TEST IS THE ANSWER'S OWN `freeTurns`, AND THAT IS THE WHOLE OF WHY THIS IS SAFE
 * FOR EVERY OTHER MEMBER ON THE SITE (R28/R29). <<< `freeTurnsPayload` returns null unless
 * `granted === true`, and the read sends `|| undefined`, so a payload carrying one IS the funnel
 * cohort and nobody else. A second cohort test typed at the call site -- a tier comparison, a
 * landing flag -- is a second answer to *is this the member the greeting was composed for*, and the
 * copy that drifts paints a stranger's drawer history over a paying member's page.
 *
 * PRESENCE, NEVER TRUTHINESS (R53/R136/R137): the read arrives off JSON. A body that is not an
 * object, a `messages` that is not a list and an empty thread each answer NOTHING rather than
 * throwing on the render path of the page her advertising lands her on (R19: a crash is not a
 * refusal).
 */
export function funnelArrivalMessages(read) {
  if (!read || typeof read !== "object" || !read.freeTurns) return null;
  const rows = Array.isArray(read.messages) ? read.messages : [];
  return rows.length ? rows : null;
}
